import React, { FormEvent, useState } from 'react';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import { routePaths } from 'components/Routes/data';
import { Box, Button, Checkbox, Text } from '@razorpay/blade/components';
import api from 'api';
import { BulkUploadDetailsSchemaContract } from 'schemas/BulkUploadDetailsSchema';
import { AppError } from 'utils/AppError';
import { BulkUploadStatus } from 'constants/bulkUpload';
import {
  FormWrapper,
  StepActionsWrapper,
  StepContentWrapper,
  StepHeader,
} from 'components/WizardViews/components';
import { GET_BULK_UPLOAD_RECORD } from 'components/BulkUploads/queries/queries';
import { FileUploadStep } from './FileUploadStep';
import { InstructionsView } from './InstructionsView';
import { DownloadSampleTemplate } from './DownloadSampleTemplate';
import { useFormWithProvider } from 'forms';
import { BulkUploadTypes } from 'constants/bulkUpload';
import { useBulkUploadType } from '../hooks/useBulkUploadType';
import useReduxSelector from 'utils/useReduxSelector';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { useWizardInfo } from 'components/WizardViews/providers/WizardInfoProvider';
import { useBulkUploadTypeConfig } from '../hooks/useBulkUploadTypeConfig';
import { useSearchParams } from 'hooks/useSearchParams';
import { PayrollMonthSelector } from './PayrollMonthSelector';
import { MonthChangeConfirmationModal } from './MonthChangeConfirmationModal';

export const BulkUploadView = () => {
  const bulkUploadType = useBulkUploadType();
  const { urlParams } = useSearchParams();
  const uploadTypeConfig = useBulkUploadTypeConfig(bulkUploadType);
  const viewSubtitle = uploadTypeConfig.initiation.step1Subtitle;
  const instructions = uploadTypeConfig.initiation.instructions;
  const organizationId = useReduxSelector(loggedInUserSelectors.organizationID) as number;
  const { updateStepStatus } = useWizardInfo();
  const pollingRequestId = `upload_${bulkUploadType}_polling_request_id`;
  const onSampleTemplateDownload = uploadTypeConfig.initiation.onSampleTemplateDownload;
  const hasMonthSelector = uploadTypeConfig.initiation.hasMonthSelector;
  const skipMonthChangeConfirmation = uploadTypeConfig.initiation.skipMonthChangeConfirmation;
  const dropdownSelectorLabel = uploadTypeConfig.initiation.dropdownSelectorLabel;
  const staticDropdownOptions = uploadTypeConfig.initiation.dropdownOptions;
  const getDropdownOptions = uploadTypeConfig.initiation.getDropdownOptions;
  const defaultDropdownValue = uploadTypeConfig.initiation.defaultDropdownValue;
  const onDropdownOptionChangeFromConfig = uploadTypeConfig.initiation.onDropdownOptionChange;
  const payrollMonth = urlParams.get('payrollMonth') || '';

  const dropdownOptions = getDropdownOptions
    ? getDropdownOptions(payrollMonth || defaultDropdownValue || '')
    : staticDropdownOptions;

  const { register: _, ...extraFieldsData } = useFormWithProvider<Record<string, any>>();
  const ExtraFields = uploadTypeConfig.initiation.ExtraFields;

  const history = useHistory();
  const [bulkUploadRecord, setBulkUploadRecord] = useState<
    BulkUploadDetailsSchemaContract[0] | null
  >(null);
  const [showMonthChangeModal, setShowMonthChangeModal] = useState(false);
  const [pendingMonthChange, setPendingMonthChange] = useState<string | null>(null);
  const [prefillEmployeeDetails, setPrefillEmployeeDetails] = useState(false);

  React.useEffect(() => {
    if (hasMonthSelector && dropdownOptions && dropdownOptions.length > 0 && !payrollMonth) {
      const defaultMonth = defaultDropdownValue ?? dropdownOptions[dropdownOptions.length - 1].value;
      history.replace({
        search: `?type=${bulkUploadType}&payrollMonth=${defaultMonth}`,
      });
    }
  }, [hasMonthSelector, payrollMonth]);  // eslint-disable-line react-hooks/exhaustive-deps

  const nextStep = () => {
    updateStepStatus(0, 'success');
    history.push({
      pathname: routePaths.bulkUploads.preview,
      search: `?type=${bulkUploadType}${payrollMonth ? `&payrollMonth=${payrollMonth}` : ''}`,
    });
  };

  const bulkUploadRecordResponse = useQuery(
    GET_BULK_UPLOAD_RECORD,
    () => {
      return api.bulkUploads.fetchBulkUploadRecordByType(bulkUploadType, payrollMonth);
    },
    {
      onSuccess: (data) => data && setBulkUploadRecord(data.length > 0 ? data[0] : null),
      onError: (error: typeof AppError) => {},
    },
  );

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const onSubmitInitiation = uploadTypeConfig.initiation.onSubmitInitiation;
    if (onSubmitInitiation) {
      onSubmitInitiation({
        extraFieldsData: extraFieldsData,
        onSuccessCb: nextStep,
        bulkUploadRecord: bulkUploadRecord,
      });
    } else {
      nextStep();
    }
  };

  const handleSampleTemplateDownload = () => {
    return onSampleTemplateDownload(organizationId, bulkUploadType, { prefillEmployeeDetails });
  };

  const handlePayrollMonthChange = (selectedMonth: string | null) => {
    if (selectedMonth && selectedMonth !== payrollMonth) {
      if (skipMonthChangeConfirmation) {
        if (onDropdownOptionChangeFromConfig) {
          onDropdownOptionChangeFromConfig(selectedMonth);
        } else {
          history.push({
            pathname: routePaths.bulkUploads.root,
            search: `?type=${bulkUploadType}&payrollMonth=${selectedMonth}`,
          });
        }
      } else {
        setPendingMonthChange(selectedMonth);
        setShowMonthChangeModal(true);
      }
    }
  };

  const handleConfirmMonthChange = () => {
    if (pendingMonthChange) {
      if (onDropdownOptionChangeFromConfig) {
        onDropdownOptionChangeFromConfig(pendingMonthChange);
      } else {
        // Default behavior if onDropdownOptionChangeFromConfig is not provided
        history.push({
          pathname: routePaths.bulkUploads.root,
          search: `?type=${bulkUploadType}&payrollMonth=${pendingMonthChange}`,
        });
      }
    }
    setShowMonthChangeModal(false);
    setPendingMonthChange(null);
  };

  const handleCancelMonthChange = () => {
    setShowMonthChangeModal(false);
    setPendingMonthChange(null);
  };

  return (
    <>
      <MonthChangeConfirmationModal
        isOpen={showMonthChangeModal}
        onDismiss={handleCancelMonthChange}
        onConfirm={handleConfirmMonthChange}
        selectedMonth={pendingMonthChange || ''}
      />
      <FormWrapper onSubmit={onSubmit}>
        <StepContentWrapper noBgIllustration maxWidth="500px">
          <StepHeader
            subtitle={uploadTypeConfig.initiation.step1Title}
            title=""
            marginBottom="spacing.4"
          />
          {viewSubtitle ? (
            <Text color="surface.text.gray.subtle" marginBottom="spacing.6">
              {viewSubtitle}
            </Text>
          ) : null}

          {hasMonthSelector && dropdownOptions && (
            <PayrollMonthSelector
              label={dropdownSelectorLabel}
              payrollMonth={payrollMonth}
              options={dropdownOptions}
              onPayrollMonthChange={handlePayrollMonthChange}
            />
          )}
          {bulkUploadType === BulkUploadTypes.TYPE_BULK_EMPLOYEE_UPDATE && (
            <Box marginBottom="spacing.4">
              <Checkbox
                isChecked={prefillEmployeeDetails}
                onChange={({ isChecked }) => setPrefillEmployeeDetails(isChecked)}
              >
                Pre-fill employee details in the template
              </Checkbox>
            </Box>
          )}
          <DownloadSampleTemplate onDownloadTemplate={handleSampleTemplateDownload} />
          <InstructionsView instructions={instructions} />
          <FileUploadStep
            onNext={nextStep}
            pollingRequestId={pollingRequestId}
            bulkUploadRecord={bulkUploadRecord}
            setBulkUploadRecord={setBulkUploadRecord}
            bulkUploadRecordResponse={bulkUploadRecordResponse}
          />
          {!!ExtraFields && <ExtraFields />}
        </StepContentWrapper>
        <StepActionsWrapper maxWidth="500px">
          <span />
          <Button
            type="submit"
            variant="primary"
            isLoading={extraFieldsData.isSubmitting}
            isDisabled={
              bulkUploadRecord
                ? bulkUploadRecord.status !== BulkUploadStatus.STATUS_VALIDATION_SUCCESS
                : true
            }>
            Upload & Preview
          </Button>
        </StepActionsWrapper>
      </FormWrapper>
    </>
  );
};
